import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  useTheme,
  Radio,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  InputAdornment,
  useMediaQuery,
  Slider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Target, 
  ChevronLeft, 
  User, 
  Activity, 
  Dumbbell,
  CheckCircle,
  Utensils
} from 'lucide-react';
import AppleSpinner from '../../common/AppleSpinner';
import agtfitService from '../service/agtfitService';

// Gradiente principal - sin cambios, se usará condicionalmente
const GRADIENT = 'linear-gradient(to bottom, #FF8C00, #FF6B00, #FF5722)';
const FIT_ORANGE = '#FF6B00';
const CREAM_COLOR = '#FFE0B2'; // Color crema para botón e iconos

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  buttonBg: '#FFFFFF',
  buttonText: '#000000'
};

// Componentes estilizados
const PageContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '800px',
  minHeight: '100vh',
  display: 'flex',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  flexDirection: 'column',
  justifyContent: 'center',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(6),
  }
}));

const ConfigCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 2px 14px rgba(0,0,0,0.06)',
  overflow: 'visible',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  maxWidth: '600px',
  width: '100%',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 30, // Más redondeado
  padding: theme.spacing(2, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '18px',
  boxShadow: 'none',
  marginTop: theme.spacing(4),
  background: GRADIENT, // Mantener gradiente naranja en todos los modos
  color: '#FFFFFF', // Texto blanco en ambos modos
  '&:hover': {
    background: GRADIENT,
    boxShadow: 'none',
    opacity: 0.9,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.5)' : 'rgba(255, 152, 0, 0.5)',
    color: '#FFFFFF', // Texto blanco incluso cuando está deshabilitado
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    fontSize: '16px',
  }
}));

// Modificado: Botón Back con los mismos estilos del botón Continue
const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: 100, // Más redondeado
  padding: theme.spacing(2, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '18px',
  boxShadow: 'none',
  marginTop: theme.spacing(4),
  marginRight: theme.spacing(2),
  background: GRADIENT, // Mantener gradiente naranja en todos los modos
  color: '#FFFFFF', // Texto blanco en ambos modos
  '&:hover': {
    background: GRADIENT,
    boxShadow: 'none',
    opacity: 0.9,
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    fontSize: '16px',
    marginRight: theme.spacing(1),
  }
}));

const Option = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: selected
    ? `2px solid ${FIT_ORANGE}` // Mantener naranja en todos los modos
    : `2px solid transparent`,
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.35)'
      : (theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED'),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& > .MuiBox-root:first-of-type': {
      marginBottom: theme.spacing(1),
    }
  }
}));

const IconContainer = styled(Box)(({ theme, color = FIT_ORANGE }) => ({
  backgroundColor: CREAM_COLOR, // Mantener color crema en todos los modos
  borderRadius: '50%',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: FIT_ORANGE, // Mantener color naranja del icono
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  }
}));

const AppleRadio = styled(Radio)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#636366' : '#8E8E93',
  '&.Mui-checked': {
    color: FIT_ORANGE, // Mantener naranja en todos los modos
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : undefined
    },
    '& input, & textarea': {
      color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: FIT_ORANGE, // Mantener naranja en todos los modos
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : undefined
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: FIT_ORANGE, // Mantener naranja en todos los modos
  },
}));

const OptionContent = styled(Box)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  }
}));

const AnimatedIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: theme.spacing(4, 0),
  '& svg': {
    fontSize: '4rem',
    color: FIT_ORANGE,
    animation: 'pulse 2s infinite'
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.1)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  }
}));

// Modificado: Contenedor de resultados con borde gris y centrado
const MacroChart = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(4), // Aumentado el padding
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  borderRadius: 16,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.05)',
  textAlign: 'center',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
  width: '100%',
  margin: '0 auto', // Centrado
}));

// Modificado: Estructura vertical para macronutrientes con ajuste para móvil
const MacroItem = styled(Box)(({ theme, delay = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: theme.spacing(0, 1.5), // Espacio horizontal entre macros
  marginBottom: theme.spacing(2),
  opacity: 0,
  animation: `fadeIn 0.5s ease-in-out ${delay}s forwards`,
  width: '30%', // Ancho fijo en desktop
  [theme.breakpoints.down('sm')]: {
    width: '30%', // Ancho fijo también en móvil para mantener estructura horizontal
    margin: theme.spacing(0, 0.5),
  }
}));

// Modificado: Contenedor para macros con mejor espaciado y centrado
const MacroItemsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center', // Centrado
  marginTop: theme.spacing(10), // Más espacio entre la barra y las macros
  marginBottom: theme.spacing(2),
  width: '100%', // Ancho completo
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(8),
  }
}));

// Modificado: Barra de macro con gradiente
const MacroBarHorizontal = styled(Box)(({ theme, delay = 0 }) => ({
  height: '10px',
  background: GRADIENT, // Mantener gradiente naranja en todos los modos
  borderRadius: '5px',
  width: '100%',
  position: 'relative',
  animation: `growWidth 1s ease-out ${delay}s forwards`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '5px',
    width: '100%',
    zIndex: -1
  },
  '@keyframes growWidth': {
    '0%': {
      width: '0%'
    },
    '100%': {
      width: '100%'
    }
  }
}));

// Título con gradiente
const GradientTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  background: GRADIENT, // Mantener gradiente naranja en todos los modos
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  textAlign: 'center'
}));

// Nuevos componentes para animaciones y transiciones
const StepContent = styled(Box)(({ theme }) => ({
  animation: 'fadeIn 0.5s ease-in-out',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const FadeInElement = styled(Box)(({ theme, delay = 0 }) => ({
  opacity: 0,
  animation: `fadeIn 0.5s ease-in-out ${delay}s forwards`,
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const AnimatedCounter = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);
  // const counterRef = useRef(null); // Unused ref
 
  useEffect(() => {
    let startTime;
    let animationFrameId;
 
    // Función para animar el contador
    const animateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
 
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const currentCount = Math.floor(progress * value);
 
      setCount(currentCount);
 
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateCount);
      } else {
        setCount(value); // Asegurar que termine con el valor exacto
      }
    };
 
    animationFrameId = requestAnimationFrame(animateCount);
 
    // Limpieza
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [value, duration]);
 
  return count;
};

// Modificado: Nueva barra horizontal de calorías con rango
const CalorieBar = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '24px',
  width: '100%',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255,255,255,0.1)'
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'),
  borderRadius: '12px',
  marginTop: theme.spacing(5), // Más espacio arriba para las calorías objetivo
  marginBottom: theme.spacing(5), // Más espacio abajo para min/max
  overflow: 'hidden'
}));

const CalorieBarFill = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '0%', // Start at 0
  background: GRADIENT, // Mantener gradiente naranja en todos los modos
  borderRadius: '12px',
  animation: 'fillBar 2s ease-out forwards',
  position: 'relative',
  '@keyframes fillBar': {
    '0%': {
      width: '0%'
    },
    '100%': {
      width: '50%' // Stop at the middle
    }
  }
}));

// Modificado: Posicionamiento de marcadores de calorías (centrado exacto)
const CalorieMarker = styled(Box)(({ theme, position }) => ({
  position: 'absolute',
  top: '-30px', // Calorías objetivo encima de la barra
  left: '50%', // Centrado exacto
  transform: 'translateX(-50%)',
  textAlign: 'center',
  opacity: 1, // Cambiado a 1 para que sea visible desde el principio
}));

// Modificado: Marcadores min/max debajo de la barra con barra vertical más gruesa
const CalorieMinMaxMarker = styled(Box)(({ theme, position }) => ({
  position: 'absolute',
  bottom: '-40px', // Mantener la posición debajo de la barra principal
  left: `${position}%`,
  transform: 'translateX(-50%)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column-reverse', // Cambiado a column-reverse para invertir el orden
  alignItems: 'center',
  opacity: 0,
  animation: 'fadeIn 0.5s ease-in-out 2s forwards',
}));

// Componente principal
const Config = ({ onUpdateUser, currentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [goal, setGoal] = useState('');
  const [approach, setApproach] = useState('');
  const [sex, setSex] = useState('');
  const [age, setAge] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [activityLevel, setActivityLevel] = useState('');
  const [strengthTraining, setStrengthTraining] = useState('');
  const [dietType, setDietType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedCalories, setCalculatedCalories] = useState(0);
  const [macros, setMacros] = useState({ protein: 0, carbs: 0, fats: 0 });
  const contentRef = useRef(null);

  // Nuevos estados para el peso objetivo
  const [goalWeight, setGoalWeight] = useState('');
  const [goalSpeed, setGoalSpeed] = useState(5); // Añadido estado para goalSpeed
  const [targetDate, setTargetDate] = useState('');

  // Calcular calorías y macros según los datos ingresados
  useEffect(() => {
    if (sex && age && height && weight && activityLevel) {
      // Calcular BMR (Basal Metabolic Rate) usando la fórmula de Mifflin-St Jeor
      let bmr = 0;
      if (sex === 'male') {
        bmr = 10 * parseFloat(weight) + 6.25 * parseFloat(height) - 5 * parseInt(age) + 5;
      } else {
        bmr = 10 * parseFloat(weight) + 6.25 * parseFloat(height) - 5 * parseInt(age) - 161;
      }

      // Multiplicador de actividad
      let activityMultiplier = 1.2; // Sedentario
      switch (activityLevel) {
        case 'lightly':
          activityMultiplier = 1.375;
          break;
        case 'moderately':
          activityMultiplier = 1.55;
          break;
        case 'very':
          activityMultiplier = 1.725;
          break;
        case 'professional':
          activityMultiplier = 1.9;
          break;
        default:
          activityMultiplier = 1.2;
      }

      // TDEE (Total Daily Energy Expenditure)
      let tdee = Math.round(bmr * activityMultiplier);

      // Ajustar según el objetivo
      if (goal === 'loss') {
        tdee = Math.round(tdee * 0.85); // Déficit del 15%
      } else if (goal === 'gain') {
        tdee = Math.round(tdee * 1.1); // Superávit del 10%
      }

      setCalculatedCalories(tdee);

      // Calcular macros según el tipo de dieta o el objetivo
      let proteinRatio, carbRatio, fatRatio;

      if (dietType) {
        // Si se ha seleccionado un tipo de dieta específico
        switch (dietType) {
          case 'recommended':
            proteinRatio = 0.3;
            carbRatio = 0.45;
            fatRatio = 0.25;
            break;
          case 'high-protein':
            proteinRatio = 0.4;
            carbRatio = 0.35;
            fatRatio = 0.25;
            break;
          case 'low-carb':
            proteinRatio = 0.35;
            carbRatio = 0.25;
            fatRatio = 0.4;
            break;
          case 'keto':
            proteinRatio = 0.3;
            carbRatio = 0.1;
            fatRatio = 0.6;
            break;
          case 'low-fat':
            proteinRatio = 0.3;
            carbRatio = 0.55;
            fatRatio = 0.15;
            break;
          default:
            proteinRatio = 0.3;
            carbRatio = 0.45;
            fatRatio = 0.25;
        }
      } else {
        // Si no hay dieta específica, basarse en el objetivo
        if (goal === 'loss') {
          proteinRatio = 0.35;
          carbRatio = 0.4;
          fatRatio = 0.25;
        } else if (goal === 'gain') {
          proteinRatio = 0.3;
          carbRatio = 0.5;
          fatRatio = 0.2;
        } else { // maintenance
          proteinRatio = 0.3;
          carbRatio = 0.45;
          fatRatio = 0.25;
        }
      }

      // Ajustar proteína si hace entrenamiento de fuerza
      if (strengthTraining === 'yes' && !dietType) {
        proteinRatio += 0.05;
        carbRatio -= 0.05;
      }

      // Calcular gramos de macros (4 cal/g para proteína y carbos, 9 cal/g para grasas)
      const proteinGrams = Math.round((tdee * proteinRatio) / 4);
      const carbGrams = Math.round((tdee * carbRatio) / 4);
      const fatGrams = Math.round((tdee * fatRatio) / 9);

      setMacros({
        protein: proteinGrams,
        carbs: carbGrams,
        fats: fatGrams
      });
    }
  }, [sex, age, height, weight, activityLevel, goal, strengthTraining, dietType]);

  // Efecto para establecer el peso objetivo inicial basado en el objetivo
  useEffect(() => {
    if (weight && goal) {
      let suggestedGoalWeight;
      const weightValue = parseFloat(weight);
      
      if (goal === 'loss') {
        // Para pérdida de peso, sugerir un 10% menos
        suggestedGoalWeight = Math.round((weightValue * 0.9) * 10) / 10;
        setGoalSpeed(5); // Valor predeterminado para pérdida
      } else if (goal === 'gain') {
        // Para ganancia de peso, sugerir un 10% más
        suggestedGoalWeight = Math.round((weightValue * 1.1) * 10) / 10;
        setGoalSpeed(3); // Valor predeterminado para ganancia
      } else {
        // Para mantenimiento, el mismo peso
        suggestedGoalWeight = weightValue;
        setGoalSpeed(0); // No hay velocidad para mantenimiento
      }
      
      setGoalWeight(suggestedGoalWeight.toString());
      
      // Calcular fecha objetivo (3 meses por defecto)
      const today = new Date();
      const targetDate = new Date();
      targetDate.setMonth(today.getMonth() + 3);
      setTargetDate(targetDate.toISOString().split('T')[0]);
    }
  }, [weight, goal]);

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [activeStep]);

  const handleNext = () => {
    // Lógica para avanzar al siguiente paso
    if (activeStep === 0 && !goal) return;
    if (activeStep === 1 && !approach) return;

    // Si seleccionó "I need a meal plan", mostrar la animación primero
    if (activeStep === 1 && approach === 'meal-plan') {
      setActiveStep(2);
      return;
    }

    // Si seleccionó "I need to track my foods", ir directamente a los datos personales
    if (activeStep === 1 && approach === 'track-foods') {
      setActiveStep(3);
      return;
    }

    // Si viene de la animación y tenía seleccionado "meal-plan", ir a datos personales
    if (activeStep === 2 && approach === 'meal-plan') {
      setActiveStep(3);
      return;
    }

    // Para el paso de datos personales
    if (activeStep === 3) {
      if (!sex || !age || !height || !weight) return;
      
      // Si viene de "track-foods", ir a nivel de actividad
      if (approach === 'track-foods') {
        setActiveStep(4);
        return;
      }
      
      // Si viene de "meal-plan", ir a nivel de actividad
      if (approach === 'meal-plan') {
        setActiveStep(4);
        return;
      }
    }

    // Para el paso de nivel de actividad
    if (activeStep === 4) {
      if (!activityLevel) return;
      
      // Si es sedentario, ir directamente a la finalización
      if (activityLevel === 'sedentary') {
        // Si viene de "track-foods", ir a resultados
        if (approach === 'track-foods') {
          setActiveStep(6);
          return;
        }
        
        // Si viene de "meal-plan", ir a tipo de dieta
        if (approach === 'meal-plan') {
          setActiveStep(5);
          return;
        }
      }
      
      // Si no es sedentario, preguntar por entrenamiento de fuerza
      if (activityLevel !== 'sedentary') {
        if (approach === 'track-foods') {
          setActiveStep(5); // Ir a pregunta de entrenamiento de fuerza
          return;
        }
        
        if (approach === 'meal-plan') {
          setActiveStep(5); // Ir a tipo de dieta
          return;
        }
      }
    }

    // Para el paso de entrenamiento de fuerza (solo para track-foods y no sedentarios)
    if (activeStep === 5 && approach === 'track-foods') {
      if (!strengthTraining) return;
      setActiveStep(6); // Ir a resultados
      return;
    }

    // Para el paso de tipo de dieta (solo para meal-plan)
    if (activeStep === 5 && approach === 'meal-plan') {
      if (!dietType) return;
      setActiveStep(6); // Ir a resultados
      return;
    }

    // Para el paso de resultados, verificar que haya un peso objetivo
    if (activeStep === 6) {
      if (!goalWeight) return;
      setActiveStep(7); // Ir al paso final
      return;
    }

    // Avanzar al siguiente paso en casos generales
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    // Lógica para retroceder
    if (activeStep === 0) return;

    // Si estamos en el paso 3 (datos personales)
    if (activeStep === 3) {
      // Si viene de "meal-plan" y pasó por la animación, volver a ella
      if (approach === 'meal-plan') {
        setActiveStep(2);
        return;
      }
      
      // Si viene de "track-foods", volver a la selección de enfoque
      if (approach === 'track-foods') {
        setActiveStep(1);
        return;
      }
    }

    // Si estamos en el paso 6 (resultados) y viene de "track-foods"
    if (activeStep === 6 && approach === 'track-foods') {
      // Si no es sedentario, volver a la pregunta de entrenamiento
      if (activityLevel !== 'sedentary') {
        setActiveStep(5);
        return;
      }
      
      // Si es sedentario, volver al nivel de actividad
      setActiveStep(4);
      return;
    }

    // Si estamos en el paso 7 (final), volver al paso 6 (resultados)
    if (activeStep === 7) {
      setActiveStep(6);
      return;
    }

    // Retroceder normalmente
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Crear objeto con los datos del perfil
      const profileData = {
        goal,
        approach,
        sex,
        age: parseInt(age),
        height: parseFloat(height),
        weight: parseFloat(weight),
        activityLevel,
        strengthTraining: strengthTraining === 'yes',
        dietType: approach === 'meal-plan' ? dietType : null,
        calculatedCalories,
        macros,
        // Añadir datos del objetivo
        goalWeight: parseFloat(goalWeight),
        goalType: goal,
        goalSpeed: goalSpeed,
        goalAutoAdjust: true,
        goalDeadline: targetDate
      };

      // Llamar al servicio para guardar el perfil
      const response = await agtfitService.createProfile(profileData);

      if (response.success) {
        // Guardar datos en localStorage como respaldo
        localStorage.setItem('agtfit_goal', goal);
        localStorage.setItem('agtfit_approach', approach);
        localStorage.setItem('agtfit_calories', calculatedCalories.toString());
        localStorage.setItem('agtfit_macros', JSON.stringify(macros));
        localStorage.setItem('agtfit_goalWeight', goalWeight);

        // Notificar al componente padre
        if (onUpdateUser) {
          onUpdateUser({
            ...profileData,
            hasProfile: true
          });
        }
      } else {
        console.error('Error al guardar perfil:', response.error);
      }
    } catch (error) {
      console.error('Error en la operación:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the current step is complete
  const isStepComplete = () => {
    switch (activeStep) {
      case 0: return !!goal;
      case 1: return !!approach;
      case 2: return true; // Animación, siempre completo
      case 3: return !!sex && !!age && !!height && !!weight;
      case 4: return !!activityLevel;
      case 5: 
        if (approach === 'track-foods') return !!strengthTraining;
        if (approach === 'meal-plan') return !!dietType;
        return false;
      case 6: return !!goalWeight; // Verificar que se haya establecido un peso objetivo
      case 7: return true; // Resultados finales, siempre completo
      default: return false;
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0: // Paso 1: What's your goal
        return (
          <StepContent sx={{ mb: 4 }}>
            <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconContainer>
                <Target size={isMobile ? 28 : 32} />
              </IconContainer>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                What's your goal?
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.3}>
              <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                We'll help you find the right calorie intake to achieve it.
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.5}>
              <Option 
                selected={goal === 'loss'} 
                onClick={() => setGoal('loss')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Fat Loss
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Optimize weight loss and preserve muscle mass
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={goal === 'loss'}
                  onChange={() => setGoal('loss')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.7}>
              <Option 
                selected={goal === 'gain'} 
                onClick={() => setGoal('gain')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Muscle Gain
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Increase your weight and get stronger
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={goal === 'gain'}
                  onChange={() => setGoal('gain')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.9}>
              <Option 
                selected={goal === 'maintenance'} 
                onClick={() => setGoal('maintenance')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Weight Maintenance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Keep your weight stable and aim for body recomposition
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={goal === 'maintenance'}
                  onChange={() => setGoal('maintenance')}
                />
              </Option>
            </FadeInElement>
          </StepContent>
        );
      
      case 1: // Paso 2: How do you want to achieve it?
        return (
          <StepContent sx={{ mb: 4 }}>
            <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconContainer>
                <Target size={isMobile ? 28 : 32} />
              </IconContainer>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                How do you want to achieve it?
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.4}>
              <Option 
                selected={approach === 'meal-plan'} 
                onClick={() => setApproach('meal-plan')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    I need a meal plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    I don't know what to eat. I need personalized meal suggestions
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={approach === 'meal-plan'}
                  onChange={() => setApproach('meal-plan')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.6}>
              <Option 
                selected={approach === 'track-foods'} 
                onClick={() => setApproach('track-foods')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    I need to track my foods
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    I know what to eat. I need to track my calories and macronutrients
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={approach === 'track-foods'}
                  onChange={() => setApproach('track-foods')}
                />
              </Option>
            </FadeInElement>
            
            <FadeInElement delay={0.8}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Don't worry, you can change this later
              </Typography>
            </FadeInElement>
          </StepContent>
        );

      case 2: // Paso 3 (si seleccionó meal-plan): Animación de fuego
        return (
          <StepContent sx={{ mb: 4, textAlign: 'center' }}>
            <FadeInElement delay={0.2}>
              <AnimatedIcon>
                <Activity size={isMobile ? 64 : 80} color={FIT_ORANGE} />
              </AnimatedIcon>
            </FadeInElement>
            
            <FadeInElement delay={0.5}>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={600} sx={{ mb: 3 }}>
                Keep going!
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.8}>
              <Typography variant="body1" sx={{ mb: 4 }}>
                Let's find out your daily calorie intake
              </Typography>
            </FadeInElement>
          </StepContent>
        );
        
      case 3: // Paso 3/4: About you
        return (
          <StepContent sx={{ mb: 4 }}>
            <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconContainer>
                <User size={isMobile ? 28 : 32} />
              </IconContainer>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                About you
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.3}>
              <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                This will help us calculate your target calories
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.5}>
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Sex</Typography>
                <RadioGroup
                  row
                  value={sex}
                  onChange={(e) => setSex(e.target.value)}
                >
                  <FormControlLabel 
                    value="male" 
                    control={<AppleRadio />} 
                    label="Male" 
                    sx={{ mr: 4 }}
                  />
                  <FormControlLabel 
                    value="female" 
                    control={<AppleRadio />} 
                    label="Female" 
                  />
                </RadioGroup>
              </FormControl>
            </FadeInElement>
            
            <FadeInElement delay={0.7}>
              <StyledTextField
                label="Age"
                type="number"
                fullWidth
                value={age}
                onChange={(e) => setAge(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">years</InputAdornment>,
                }}
                sx={{ mb: 3 }}
              />
            </FadeInElement>
            
            <FadeInElement delay={0.9}>
              <StyledTextField
                label="Height"
                type="number"
                fullWidth
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                }}
                sx={{ mb: 3 }}
              />
            </FadeInElement>
            
            <FadeInElement delay={1.1}>
              <StyledTextField
                label="Weight"
                type="number"
                fullWidth
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
              />
            </FadeInElement>
          </StepContent>
        );
        
      case 4: // Paso 4/5: Activity level
        return (
          <StepContent sx={{ mb: 4 }}>
            <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconContainer>
                <Activity size={isMobile ? 28 : 32} />
              </IconContainer>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                What's your activity level?
              </Typography>
            </FadeInElement>
            
            <FadeInElement delay={0.3}>
              <Option 
                selected={activityLevel === 'sedentary'} 
                onClick={() => setActivityLevel('sedentary')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Sedentary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Little or no exercise
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={activityLevel === 'sedentary'}
                  onChange={() => setActivityLevel('sedentary')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.4}>
              <Option 
                selected={activityLevel === 'lightly'} 
                onClick={() => setActivityLevel('lightly')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Lightly Active
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exercise 1-3 days per week
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={activityLevel === 'lightly'}
                  onChange={() => setActivityLevel('lightly')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.5}>
              <Option 
                selected={activityLevel === 'moderately'} 
                onClick={() => setActivityLevel('moderately')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Moderately Active
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exercise 3-5 days per week
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={activityLevel === 'moderately'}
                  onChange={() => setActivityLevel('moderately')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.6}>
              <Option 
                selected={activityLevel === 'very'} 
                onClick={() => setActivityLevel('very')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Very Active
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Exercise 6-7 days per week
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={activityLevel === 'very'}
                  onChange={() => setActivityLevel('very')}
                />
              </Option>
            </FadeInElement>

            <FadeInElement delay={0.7}>
              <Option 
                selected={activityLevel === 'professional'} 
                onClick={() => setActivityLevel('professional')}
              >
                <OptionContent>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                    Professional Athlete
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Intense exercise 6-7 days per week
                  </Typography>
                </OptionContent>
                <AppleRadio 
                  checked={activityLevel === 'professional'}
                  onChange={() => setActivityLevel('professional')}
                />
              </Option>
            </FadeInElement>
            
            <FadeInElement delay={0.9}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Don't worry, you can change this later
              </Typography>
            </FadeInElement>
          </StepContent>
        );
        
      case 5:
        // Si viene de "track-foods", mostrar pregunta de entrenamiento de fuerza
        if (approach === 'track-foods') {
          return (
            <StepContent sx={{ mb: 4 }}>
              <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IconContainer>
                  <Dumbbell size={isMobile ? 28 : 32} />
                </IconContainer>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                  Do you do strength training?
                </Typography>
              </FadeInElement>
              
              <FadeInElement delay={0.3}>
                <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                  This is key to determine your protein intake
                </Typography>
              </FadeInElement>
              
              <FadeInElement delay={0.5}>
                <Option 
                  selected={strengthTraining === 'yes'} 
                  onClick={() => setStrengthTraining('yes')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      Yes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      I do strength training regularly
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={strengthTraining === 'yes'}
                    onChange={() => setStrengthTraining('yes')}
                  />
                </Option>
              </FadeInElement>

              <FadeInElement delay={0.7}>
                <Option 
                  selected={strengthTraining === 'no'} 
                  onClick={() => setStrengthTraining('no')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      No
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      I don't do strength training regularly
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={strengthTraining === 'no'}
                    onChange={() => setStrengthTraining('no')}
                  />
                </Option>
              </FadeInElement>
            </StepContent>
          );
        }
        
        // Si viene de "meal-plan", mostrar selección de tipo de dieta
        if (approach === 'meal-plan') {
          return (
            <StepContent sx={{ mb: 4 }}>
              <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IconContainer>
                  <Utensils size={isMobile ? 28 : 32} />
                </IconContainer>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                  Which type of diet do you want?
                </Typography>
              </FadeInElement>
              
              <FadeInElement delay={0.3}>
                <Option 
                  selected={dietType === 'recommended'} 
                  onClick={() => setDietType('recommended')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      Recommended
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best match for you, optimal mix of protein, carbs, and fats
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={dietType === 'recommended'}
                    onChange={() => setDietType('recommended')}
                  />
                </Option>
              </FadeInElement>

              <FadeInElement delay={0.4}>
                <Option 
                  selected={dietType === 'high-protein'} 
                  onClick={() => setDietType('high-protein')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      High Protein
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      More protein, less carbs and fats
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={dietType === 'high-protein'}
                    onChange={() => setDietType('high-protein')}
                  />
                </Option>
              </FadeInElement>

              <FadeInElement delay={0.5}>
                <Option 
                  selected={dietType === 'low-carb'} 
                  onClick={() => setDietType('low-carb')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      Low-Carb
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fewer carbs, higher fats and moderate protein
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={dietType === 'low-carb'}
                    onChange={() => setDietType('low-carb')}
                  />
                </Option>
              </FadeInElement>

              <FadeInElement delay={0.6}>
                <Option 
                  selected={dietType === 'keto'} 
                  onClick={() => setDietType('keto')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      Keto
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Very low in carbs, high fat and moderate protein
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={dietType === 'keto'}
                    onChange={() => setDietType('keto')}
                  />
                </Option>
              </FadeInElement>

              <FadeInElement delay={0.7}>
                <Option 
                  selected={dietType === 'low-fat'} 
                  onClick={() => setDietType('low-fat')}
                >
                  <OptionContent>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={500}>
                      Low-fat
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fewer fats, higher carbs and moderate protein
                    </Typography>
                  </OptionContent>
                  <AppleRadio 
                    checked={dietType === 'low-fat'}
                    onChange={() => setDietType('low-fat')}
                  />
                </Option>
              </FadeInElement>
            </StepContent>
          );
        }
        
        return null;
        
      case 6: // Paso para establecer el peso objetivo
        return (
          <StepContent sx={{ mb: 4, textAlign: 'center' }}>
            <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconContainer>
                <Target size={isMobile ? 28 : 32} />
              </IconContainer>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600}>
                Set Your Target Weight
              </Typography>
            </FadeInElement> 
            
            <FadeInElement delay={0.3}>
              <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
                {goal === 'loss' 
                  ? 'A healthy weight loss is about 0.5-1 kg per week.' 
                  : goal === 'gain' 
                    ? 'A healthy weight gain is about 0.25-0.5 kg per week.'
                    : 'Maintaining your weight while improving body composition.'}
              </Typography>
            </FadeInElement>
            
            <MacroChart>
              <FadeInElement delay={0.5} sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Weight
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {weight} kg
                </Typography>
              </FadeInElement>
              
              <FadeInElement delay={0.7}>
                <StyledTextField
                  label="Target Weight"
                  type="number"
                  fullWidth
                  value={goalWeight}
                  onChange={(e) => setGoalWeight(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                />
              </FadeInElement>
              
              <FadeInElement delay={0.9} sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'left' }}>
                  Estimated time to reach goal
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Slower
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Faster
                  </Typography>
                </Box>
                
                <Slider
                  value={goal === 'maintenance' ? 0 : goalSpeed}
                  onChange={(e, newValue) => setGoalSpeed(newValue)}
                  disabled={goal === 'maintenance'}
                  min={1}
                  max={10}
                  marks
                  sx={{
                    '& .MuiSlider-thumb': {
                      color: FIT_ORANGE,
                    },
                    '& .MuiSlider-track': {
                      color: FIT_ORANGE,
                    },
                    '& .MuiSlider-rail': {
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                    }
                  }}
                />
                
                <Typography variant="body2" fontWeight={500} sx={{ mt: 1 }}>
                  {goal === 'loss' 
                    ? goalSpeed <= 3 
                      ? 'About 16 weeks (safer, preserves muscle)' 
                      : goalSpeed <= 6 
                        ? 'About 10 weeks (moderate pace)' 
                        : 'About 6-8 weeks (faster, may lose muscle)'
                    : goal === 'gain'
                      ? goalSpeed <= 3 
                        ? 'About 20 weeks (cleaner bulk)' 
                        : goalSpeed <= 6 
                          ? 'About 12 weeks (moderate pace)' 
                          : 'About 8 weeks (faster, may gain more fat)'
                      : 'Ongoing maintenance'}
                </Typography>
              </FadeInElement>
            </MacroChart>
          </StepContent>
        );

      case 7: // Paso final: Resultados
        return (
          <StepContent sx={{ mb: 4, textAlign: 'center' }}>
            <FadeInElement delay={0.1} sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CheckCircle size={isMobile ? 64 : 80} color={FIT_ORANGE} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                You're all set!
              </Typography>
            </FadeInElement> 
            
            <MacroChart>    
              <FadeInElement delay={0.3}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your daily calorie target
                </Typography>
              </FadeInElement>
              
              <FadeInElement delay={0.5} sx={{ position: 'relative' }}>
                <CalorieBar>
                  <CalorieBarFill />
                </CalorieBar>
                
                <CalorieMarker position={50}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                    <AnimatedCounter value={calculatedCalories} duration={2} />
                </Typography>
                </CalorieMarker>
                
                {/* Marcadores min/max debajo de la barra con barra vertical más gruesa */}
                <CalorieMinMaxMarker position={10}>
                  <Typography variant="body2" fontWeight={600}>
                    {Math.round(calculatedCalories * 0.9)}
                  </Typography>
                  <Box sx={{ height: '15px', width: '2px', bgcolor: 'text.secondary', mt: 1 }} />
                </CalorieMinMaxMarker>
                
                <CalorieMinMaxMarker position={90}>
                  <Typography variant="body2" fontWeight={600}>
                    {Math.round(calculatedCalories * 1.1)}
                  </Typography>
                  <Box sx={{ height: '15px', width: '2px', bgcolor: 'text.secondary', mt: 1 }} />
                </CalorieMinMaxMarker>
              </FadeInElement>
              
              {/* Contenedor para los macronutrientes en estructura vertical, optimizado para móvil */}
              <MacroItemsContainer>
                <MacroItem delay={0.8}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Protein
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {macros.protein}g
                  </Typography>
                  <MacroBarHorizontal delay={0.8} />
                </MacroItem>
                
                <MacroItem delay={1.0}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Carbs
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {macros.carbs}g
                  </Typography>
                  <MacroBarHorizontal delay={1.0} />
                </MacroItem>
                
                <MacroItem delay={1.2}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Fats
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {macros.fats}g
                  </Typography>
                  <MacroBarHorizontal delay={1.2} />
                </MacroItem>
              </MacroItemsContainer>
            </MacroChart>
            
            <FadeInElement delay={1.4} sx={{ mt: 4 }}>
              <Typography variant="body1" fontWeight={500}>
                Your target weight: {goalWeight} kg
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We'll help you track your progress toward this goal
              </Typography>
            </FadeInElement>
          </StepContent>
        );
        
      default:
        return 'Unknown step';
    }
  };

  return (
    <PageContainer ref={contentRef}>
      <ConfigCard>
        {activeStep === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            mb: isMobile ? 2 : 4, 
            textAlign: 'center',
            alignItems: 'center'
          }}>
            <GradientTitle variant={isMobile ? "h5" : "h4"}>
              AGT Fit
            </GradientTitle>
          </Box>
        )}

        {getStepContent()}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: isMobile && activeStep === 0 ? 'column' : 'row'
        }}>
          {activeStep > 0 && (
            <BackButton
              onClick={handleBack}
              disabled={isSubmitting}
              fullWidth={isMobile && activeStep === 0}
            >
              <ChevronLeft size={20} color="white" />
            </BackButton>
          )}
          
          {activeStep === 7 ? (
            <ActionButton 
              variant="contained"
              disableElevation
              onClick={handleComplete}
              disabled={isSubmitting}
              fullWidth={isMobile || activeStep === 0}
            >
              {isSubmitting ? 
                <AppleSpinner sizeDesktop={24} sizeMobile={20} /> : 
                'Start My Journey'
              }
            </ActionButton>
          ) : (
            <ActionButton 
              variant="contained"
              disableElevation
              disabled={!isStepComplete() || isSubmitting}
              onClick={handleNext}
              fullWidth={isMobile || activeStep === 0}
            >
              {activeStep === 2 ? 'Continue' : 'Next'}
            </ActionButton>
          )}
        </Box>
      </ConfigCard>
    </PageContainer>
  );
};

export default Config;